import { useEffect, useState } from 'react';
import { Button, Checkbox, EntityChip, LiveStepList, type LiveOperationStep } from '@/components/ui';
import { useExpansionMemory } from '@/components/layout/workspace/useExpansionMemory';

type Run = { current: number; progress: number; phase: 'paused' | 'running' | 'failed' | 'done' };
const INITIAL: Run = { current: 1, progress: 40, phase: 'paused' };
const TITLES = ['Получить записи', 'Проверить связи', 'Составить итог'];
export function LiveStepsDemo({
  scope,
  onEntityOpen,
}: {
  scope: string;
  onEntityOpen: (id: string) => void;
}) {
  const [run, setRun] = useState<Run>(INITIAL);
  const [ai, setAi] = useState(false);
  const [fail, setFail] = useState(false);
  const [expanded, setExpanded] = useExpansionMemory(scope + '.steps', ['step-1']);
  const advance = (previous: Run): Run =>
    fail
      ? { ...previous, phase: 'failed' }
      : previous.current >= TITLES.length - 1
        ? { ...previous, progress: 100, phase: 'done' }
        : { ...previous, current: previous.current + 1, progress: 0 };
  useEffect(() => {
    if (run.phase !== 'running') return;
    const timer = window.setInterval(
      () =>
        setRun((previous) => {
          if (previous.phase !== 'running') return previous;
          if (previous.progress < 90) return { ...previous, progress: previous.progress + 10 };
          if (fail) return { ...previous, phase: 'failed' };
          return previous.current >= TITLES.length - 1
            ? { ...previous, progress: 100, phase: 'done' }
            : { ...previous, current: previous.current + 1, progress: 0 };
        }),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [run.phase, fail]);
  const steps: LiveOperationStep[] = TITLES.map((label, index) => ({
    id: 'step-' + index,
    label,
    status:
      run.phase === 'done' || index < run.current
        ? 'done'
        : index > run.current
          ? 'pending'
          : run.phase === 'failed'
            ? 'failed'
            : 'active',
    note:
      index === run.current && run.phase !== 'done'
        ? run.phase === 'failed'
          ? 'Нет ответа'
          : run.progress + ' %'
        : undefined,
    progress: run.progress,
    details: (
      <div className="flex flex-col gap-3">
        <p>
          {index === 0
            ? 'Получены 12 записей из каталога А. Все идентификаторы различаются.'
            : index === 1
              ? run.phase === 'failed' && run.current === index
                ? 'Источник Б не ответил. Уже полученные записи сохранены; можно повторить этот шаг.'
                : run.current > index || run.phase === 'done'
                  ? 'Проверены все 8 связей: обе стороны каждой связи существуют.'
                  : 'Проверяется, что обе стороны каждой из 8 связей существуют. Доля относится только к текущему шагу.'
              : run.phase === 'failed' && run.current === index
                ? 'Источник Б не ответил при подготовке итога. Результаты предыдущих шагов сохранены.'
                : run.phase === 'done'
                  ? 'Все 8 связей проверены. Итог относится к данным этого запуска.'
                  : 'Итог появится после проверки связей.'}
        </p>
        <div className="flex flex-wrap gap-2">
          <EntityChip
            kind="task"
            name={index === 0 ? 'Запись А-12' : 'Запись Б-08'}
            onClick={() => onEntityOpen(index === 0 ? 'record-a12' : 'record-b08')}
          />
        </div>
      </div>
    ),
  }));
  return (
    <div data-slot="live-steps-demo" className="flex min-w-0 flex-col gap-4">
      <p className="text-sm text-fg-muted">
        Пробный ход проверки: можно пройти шаги вручную или запустить их смену.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={run.phase === 'done' || run.phase === 'failed'}
          onClick={() =>
            setRun((previous) => ({
              ...previous,
              phase: previous.phase === 'running' ? 'paused' : 'running',
            }))
          }
        >
          {run.phase === 'running' ? 'Пауза' : 'Запустить'}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={run.phase === 'running' || run.phase === 'done'}
          onClick={() => {
            if (run.phase === 'failed') {
              setFail(false);
              setRun((previous) => ({ ...previous, progress: 0, phase: 'paused' }));
            } else setRun(advance);
          }}
        >
          {run.phase === 'failed' ? 'Повторить шаг' : 'Следующий шаг'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setRun(INITIAL);
            setFail(false);
          }}
        >
          Сначала
        </Button>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-3">
        <Checkbox
          label="Проверку запустил ИИ"
          checked={ai}
          onCheckedChange={(value) => setAi(value === true)}
        />
        <Checkbox
          label="Сбой текущего шага"
          checked={fail}
          disabled={run.phase === 'done' || run.phase === 'failed'}
          onCheckedChange={(value) => setFail(value === true)}
        />
      </div>
      <LiveStepList
        steps={steps}
        paused={run.phase === 'paused'}
        initiator={ai ? 'ai' : 'system'}
        expanded={expanded}
        onExpandedChange={setExpanded}
        headingLevel={4}
        summary={
          run.phase === 'done' ? (
            <p>
              <strong>Проверка завершена.</strong> 12 записей и 8 связей проверены, ошибок не найдено.
            </p>
          ) : run.phase === 'failed' ? (
            <p>
              <strong>Проверка остановлена.</strong> Источник Б не ответил. Повтори текущий шаг, чтобы
              продолжить.
            </p>
          ) : undefined
        }
      />
    </div>
  );
}
